import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import {
    Link, useHistory, useLocation,
} from 'react-router-dom';
import queryString from 'query-string';
import TrackVisibility from 'react-on-screen';
import HeaderTags from '../../../common/headerTags/HeaderTags';
import NewsItemComponent from '../containers/NewsItemContainer';
import Spinner from '../../../common/Spinner/Spinner';
import ErrorPage from '../../../common/errorPage/ErrorPage';
import { parseNewsPage, newsPagePath } from '../../../../api/utils/newsPagination';

const headerTagsJson = require('./headerTags.json');

function NewsPageComponent({
    archivePages, newsCategories, category = 'tudo', getNewsArchivePage, getNewsCategoryIfNeeded,
}) {
    const history = useHistory();
    const location = useLocation();
    const page = parseNewsPage(queryString.parse(location.search).page);
    const entry = archivePages[`${category}:${page}`];
    const headerTags = headerTagsJson[category] || headerTagsJson.tudo;
    const selectedCategory = newsCategories.find((item) => item.slug === category);
    const heading = category === 'tudo' ? 'Últimas notícias' : `Notícias de ${selectedCategory?.name || category}`;

    useEffect(() => {
        getNewsCategoryIfNeeded();
    }, [getNewsCategoryIfNeeded]);

    useEffect(() => {
        if (page) getNewsArchivePage(page, category).catch(() => {});
    }, [getNewsArchivePage, page, category]);

    if (!page || entry?.notFound) {
        return <ErrorPage
            errorNumber={404}
            backLink={newsPagePath(category, 1)}
            title="Página de notícias não encontrada"
            message="Acesse a primeira página para continuar lendo."
            buttonText="Ver notícias"
        />;
    }

    return (
        <section className="tw-w-full">
            <HeaderTags
                title={`${headerTags.title}${page > 1 ? ` — Página ${page}` : ''}`}
                meta={headerTags.meta}
                canonicalPath={newsPagePath(category, page)}
            />
            <div className="tw-text-center">
                <h1 className="tw-text-black tw-normal-case tw-font-bold">
                    {heading}
                </h1>
                <p>
                    {headerTags.meta.description}
                </p>
                <label className="tw-block tw-my-7" htmlFor="news-category">
                    Categoria
                    <select
                        id="news-category"
                        className="tw-ml-3 tw-p-3 tw-border tw-border-gray-300 tw-bg-white"
                        value={category}
                        onChange={(event) => history.push(newsPagePath(event.target.value, 1))}
                    >
                        <option value="tudo">Todas as categorias</option>
                        {newsCategories.map((item) => (
                            <option key={item.slug} value={item.slug}>{item.name}</option>
                        ))}
                    </select>
                </label>
            </div>
            {entry?.error ? (
                <div role="alert" className="tw-text-center tw-my-8">
                    <p>Não foi possível carregar as notícias.</p>
                    <button type="button" onClick={() => getNewsArchivePage(page, category).catch(() => {})}>Tentar novamente</button>
                </div>
            ) : !entry || entry.loading ? <Spinner /> : (
                <React.Fragment>
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-6 tw-my-8">
                        {entry.items.map((data) => (
                            <TrackVisibility key={data.id} className="newsItemWrapper">
                                <NewsItemComponent data={data} isFeatured={false} showContent selectedCategory={category} />
                            </TrackVisibility>
                        ))}
                    </div>
                    {!entry.items.length && <p>Não há notícias nesta categoria.</p>}
                    <nav aria-label="Paginação das notícias" className="tw-flex tw-justify-center tw-items-center tw-gap-6 tw-my-8">
                        {page > 1 && <Link rel="prev" to={newsPagePath(category, page - 1)}>Anterior</Link>}
                        <span aria-current="page">{`Página ${page}`}</span>
                        {entry.hasNext && <Link rel="next" to={newsPagePath(category, page + 1)}>Próxima página</Link>}
                    </nav>
                </React.Fragment>
            )}
        </section>
    );
}

NewsPageComponent.propTypes = exact({
    archivePages: PropTypes.object.isRequired,
    newsCategories: PropTypes.array.isRequired,
    category: PropTypes.string,
    getNewsArchivePage: PropTypes.func.isRequired,
    getNewsCategoryIfNeeded: PropTypes.func.isRequired,
});

export default NewsPageComponent;
